import { ClaimSessionStatusToText } from '@helpers';
import _ from 'lodash';
import { useRouter } from 'nuxt/app';
import { watch } from 'vue';

import { useClaimStore } from '~/store/claim';
import { useOrdersStore } from '~/store/orders';

export default defineNuxtPlugin(() => {
  const router = useRouter();

  const orderStore = useOrdersStore();
  const claimStore = useClaimStore();

  const DEFAULT_CLAIM = {
    customer: {},
    fulfillments: {},
    resolution: {
      desiredMethod: null,
      updatedShippingAddress: null,
    },
    signature: null,
  };

  // Deeply compares two objects and returns an array of { path, oldValue, newValue } for all differences.
  const diffObjects = (a, b, path = '') => {
    if (_.isEqual(a, b)) return [];
    if (!_.isObject(a) || !_.isObject(b) || a == null || b == null) {
      return [{ newValue: b, oldValue: a, path }];
    }

    const keys = _.union(Object.keys(a), Object.keys(b));

    return keys.flatMap(k => diffObjects(a[k], b[k], path ? `${path}.${k}` : k));
  };

  router.beforeEach(async to => {
    if (!orderStore.currentOrder?.systemOrderId) return;

    const status = Object.entries(ClaimSessionStatusToText).find(([, path]) => to.path.endsWith(path))?.[0];

    if (status && claimStore.claimSession?.status !== status) {
      await claimStore.updateClaimSession({
        email: orderStore.currentOrder.customerEmail,
        orderId: orderStore.currentOrder.systemOrderId,
        status,
      });
    }
  });

  // Watch for changes to claim data and sync metadata
  watch(
    () => ({
      claim: claimStore.claim,
      currentShipmentId: claimStore.currentShipmentId,
    }),
    _.debounce(async newValue => {
      const oldValue = claimStore.claimSession.metadata;

      // Only update if we have a session and order

      if (!newValue.currentShipmentId) return;
      if (!claimStore.claimSession || !orderStore.currentOrder?.systemOrderId) return;

      // Skip if claim is empty/default state
      if (_.isEqual(newValue.claim, DEFAULT_CLAIM) && !newValue.currentShipmentId) return;

      // Skip if no fulfillments with current shipment
      if (newValue.currentShipmentId && !newValue.claim?.fulfillments?.[newValue.currentShipmentId]) return;

      const differences = diffObjects(oldValue, newValue);

      const shipmentChanged = newValue.currentShipmentId !== oldValue?.currentShipmentId;
      const claimChanged = differences.length > 0;

      if (claimChanged || shipmentChanged) {
        await claimStore.updateClaimSession({
          email: orderStore.currentOrder.customerEmail,
          metadata: {
            claim: newValue.claim,
            currentShipmentId: newValue.currentShipmentId,
          },
          orderId: orderStore.currentOrder.systemOrderId,
        });
      }
    }, 300),
    { deep: true },
  );
});
